import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { ChatContainer, ChatButton, ChatWindow, WindowHeader, MessageList, MessageItem, InputContainer, SendButton, StatusLabel, TypingIndicator } from './ChatBot.styled';
import { chatService } from '../../services/chatService';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export const ChatBot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [chatSessionId, setChatSessionId] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Synchronous re-entry guard: prevents a second send (e.g. Enter mid-stream) from
  // appending another bubble while streaming callbacks still target the last one.
  const isStreamingRef = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleToggleChat = () => {
    if (!isOpen) {
      setMessages([]);
      setChatSessionId(crypto.randomUUID());
    }
    setIsOpen(!isOpen);
  };

  const updateLastAssistant = (updater: (content: string) => string) => {
    setMessages(prev => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last && last.role === 'assistant') {
        next[next.length - 1] = { ...last, content: updater(last.content) };
      }
      return next;
    });
  };

  const handleSendMessage = async () => {
    if (isStreamingRef.current) return;
    if (!inputMessage.trim()) return;
    isStreamingRef.current = true;

    const content = inputMessage.trim();
    // Append the user message plus an empty assistant placeholder we stream into.
    setMessages(prev => [...prev, { role: 'user', content }, { role: 'assistant', content: '' }]);
    setInputMessage('');
    setIsLoading(true);
    setStatus('');

    // Accumulate locally so the state updater stays pure. Each tool call (status event)
    // marks a turn boundary; insert a paragraph break before the next turn's text so the
    // agent's "Let me… / Now let me…" steps don't run together while streaming.
    let acc = '';
    let needSeparator = false;

    try {
      await chatService.sendMessageStream(content, chatSessionId, {
        onStatus: (label) => {
          setStatus(label);
          if (acc) needSeparator = true;
        },
        onDelta: (text) => {
          setStatus('');
          if (needSeparator) {
            acc += '\n\n';
            needSeparator = false;
          }
          acc += text;
          updateLastAssistant(() => acc);
        },
        onDone: ({ response }) => {
          setStatus('');
          acc = response;
          updateLastAssistant(() => acc);
        },
        onError: (message) => {
          setStatus('');
          acc = message;
          updateLastAssistant(() => acc);
        },
      });
    } catch (error) {
      console.error('Failed to get response:', error);
      updateLastAssistant(prev => prev || 'Sorry, something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
      setStatus('');
      isStreamingRef.current = false;
    }
  };

  return (
    <ChatContainer>
      <ChatButton onClick={handleToggleChat} aria-label={isOpen ? 'Close chat' : 'Open chat'}>
        {isOpen ? (
          '×'
        ) : (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.6 8.6 0 0 1-3.5-.74L3 21l1.74-6A8.38 8.38 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z" />
          </svg>
        )}
      </ChatButton>

      {isOpen && (
        <ChatWindow>
          <WindowHeader>
            <p>OpenTelemetry Demo assistant</p>
            <span>Ask about gear, orders, or the night sky</span>
          </WindowHeader>
          <MessageList>
            {messages.map((message, index) => {
              const isLastAssistant = index === messages.length - 1 && message.role === 'assistant';
              // Keep the indicator up for the whole turn — the agent streams text, calls
              // tools, and streams more, so it's still working even once partial text shows.
              const showThinking = isLastAssistant && isLoading;
              return (
                <MessageItem key={index} role={message.role}>
                  {message.role === 'assistant'
                    ? <ReactMarkdown>{message.content}</ReactMarkdown>
                    : message.content}
                  {showThinking && (
                    <>
                      {status && <StatusLabel>{status}</StatusLabel>}
                      <TypingIndicator aria-label="Assistant is thinking">
                        <span />
                        <span />
                        <span />
                      </TypingIndicator>
                    </>
                  )}
                </MessageItem>
              );
            })}
            <div ref={messagesEndRef} />
          </MessageList>
          
          <InputContainer>
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !isLoading && handleSendMessage()}
              placeholder="Type your message..."
            />
            <SendButton onClick={handleSendMessage} disabled={isLoading}>
              Send
            </SendButton>
          </InputContainer>
        </ChatWindow>
      )}
    </ChatContainer>
  );
}; 
